import { Button, Image, Input, Popconfirm, Tag, Tooltip, Upload } from 'antd';
import {
    DeleteOutlined, EditOutlined, CheckOutlined, CloseOutlined, CloudUploadOutlined, FileImageOutlined,
    FilePdfOutlined, PaperClipOutlined, SendOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { serverFetch, fileUrl } from '@server';
import { showError, fmtDateTime } from '@extensions/utils';
import PdfModal, { isPdfFile } from '@controls/pdf-viewer/pdf-modal';
import UserAvatar from './user-avatar';

const fmtSize = (size) => (size >= 1024 * 1024
    ? `${(size / 1024 / 1024).toFixed(1)} МБ`
    : `${Math.max(1, Math.round(size / 1024))} КБ`);

// Комментарии к задаче (ownerVid = 1) с файловыми вложениями — в виде ленты-чата
const TaskComments = ({ taskId }) => {
    const user = useSelector((s) => s.user);
    const [items, setItems] = useState([]);
    const [text, setText] = useState('');
    const [files, setFiles] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [sending, setSending] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');
    const [pdfModal, setPdfModal] = useState(null);
    const dragDepth = useRef(0);

    const canEdit = (c) => user?.isAdmin || user?.isSuperadmin || c.userId === user?.userId;

    const load = useCallback(() => {
        serverFetch(`comment?ownerVid=1&ownerId=${taskId}`)
            .then(setItems)
            .catch(showError);
    }, [taskId]);

    useEffect(() => { load(); }, [load]);

    const uploadFiles = (commentId, fileList) => {
        const fd = new FormData();
        fileList.forEach((f) => fd.append('files', f));
        return serverFetch(`file?ownerVid=1&ownerId=${commentId}`, { method: 'POST', formData: fd });
    };

    const addFiles = (list) => setFiles((l) => [...l, ...Array.from(list)]);

    const onAdd = () => {
        if ((!text.trim() && !files.length) || sending) return;
        setSending(true);
        serverFetch('comment', {
            method: 'POST',
            bodyData: { ownerVid: 1, ownerId: taskId, body: text.trim() },
        })
            .then((c) => (files.length ? uploadFiles(c.id, files) : null))
            .then(() => { setText(''); setFiles([]); load(); })
            .catch(showError)
            .finally(() => setSending(false));
    };

    const onAttach = (commentId, file) => {
        uploadFiles(commentId, [file])
            .then(load)
            .catch(showError);
    };

    const onDeleteFile = (id) => {
        serverFetch(`file/${id}`, { method: 'DELETE' })
            .then(load)
            .catch(showError);
    };

    const onSaveEdit = (id) => {
        serverFetch(`comment/${id}`, { method: 'PUT', bodyData: { body: editText } })
            .then(() => { setEditingId(null); load(); })
            .catch(showError);
    };

    const onDelete = (id) => {
        serverFetch(`comment/${id}`, { method: 'DELETE' })
            .then(load)
            .catch(showError);
    };

    // Drag-and-drop файлов на поле ввода
    const hasFiles = (e) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const dropHandlers = {
        onDragEnter: (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragOver(true); },
        onDragOver: (e) => { if (hasFiles(e)) e.preventDefault(); },
        onDragLeave: () => { dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragOver(false); } },
        onDrop: (e) => {
            if (!hasFiles(e)) return;
            e.preventDefault();
            dragDepth.current = 0;
            setDragOver(false);
            addFiles(e.dataTransfer.files);
        },
    };

    // Вставка картинок из буфера обмена
    const onPaste = (e) => {
        const pasted = Array.from(e.clipboardData?.files ?? []);
        if (pasted.length) {
            e.preventDefault();
            addFiles(pasted);
        }
    };

    const onComposerKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            onAdd();
        }
    };

    const renderAttachments = (c) => {
        const list = c.files ?? [];
        if (!list.length) return null;
        const images = list.filter((f) => f.isImage);
        const pdfs = list.filter((f) => !f.isImage && isPdfFile(f));
        const others = list.filter((f) => !f.isImage && !isPdfFile(f));
        const delBtn = (f) => canEdit(c) && (
            <Popconfirm title="Удалить файл?" onConfirm={() => onDeleteFile(f.id)} okText="Да" cancelText="Нет">
                <DeleteOutlined className="task-attachment-del" onClick={(e) => e.stopPropagation()} />
            </Popconfirm>
        );
        return (
            <div className="task-attachments">
                {images.length > 0 && (
                    <Image.PreviewGroup>
                        {images.map((f) => (
                            <span key={f.id} className="task-attachment-img">
                                <Image
                                    width={112}
                                    height={84}
                                    style={{ objectFit: 'cover', borderRadius: 8 }}
                                    src={fileUrl(f.id, true)}
                                    alt={f.name}
                                />
                                {delBtn(f)}
                            </span>
                        ))}
                    </Image.PreviewGroup>
                )}
                {pdfs.map((f) => (
                    <span key={f.id} className="task-attachment-file">
                        <a href={fileUrl(f.id)} onClick={(e) => { e.preventDefault(); setPdfModal(f); }}>
                            <FilePdfOutlined /> {f.name}
                        </a>
                        <span className="task-attachment-size">{fmtSize(f.size)}</span>
                        {delBtn(f)}
                    </span>
                ))}
                {others.map((f) => (
                    <span key={f.id} className="task-attachment-file">
                        <a href={fileUrl(f.id)} target="_blank" rel="noreferrer">
                            <PaperClipOutlined /> {f.name}
                        </a>
                        <span className="task-attachment-size">{fmtSize(f.size)}</span>
                        {delBtn(f)}
                    </span>
                ))}
            </div>
        );
    };

    const renderTools = (c) => {
        if (editingId === c.id) {
            return (
                <span className="task-msg-tools" style={{ opacity: 1 }}>
                    <Button size="small" type="text" icon={<CheckOutlined />} onClick={() => onSaveEdit(c.id)} />
                    <Button size="small" type="text" icon={<CloseOutlined />} onClick={() => setEditingId(null)} />
                </span>
            );
        }
        if (!canEdit(c)) return null;
        return (
            <span className="task-msg-tools">
                <Upload multiple showUploadList={false} beforeUpload={(f) => { onAttach(c.id, f); return false; }}>
                    <Tooltip title="Прикрепить файл"><Button size="small" type="text" icon={<PaperClipOutlined />} /></Tooltip>
                </Upload>
                <Tooltip title="Изменить">
                    <Button size="small" type="text" icon={<EditOutlined />}
                        onClick={() => { setEditingId(c.id); setEditText(c.body); }} />
                </Tooltip>
                <Popconfirm title="Удалить комментарий?" onConfirm={() => onDelete(c.id)} okText="Да" cancelText="Нет">
                    <Button size="small" type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
            </span>
        );
    };

    return (
        <div>
            <div className="task-chat">
                {items.length === 0 && <div className="task-chat-empty">Пока тихо — начните обсуждение</div>}
                {items.map((c) => {
                    const name = c.userName || `#${c.userId}`;
                    return (
                        <div key={c.id} className={`task-msg${c.userId === user?.userId ? ' own' : ''}`}>
                            <UserAvatar name={name} size={32} />
                            <div className="task-msg-bubble">
                                <div className="task-msg-head">
                                    <b>{name}</b>
                                    <span>{fmtDateTime(c.modified || c.created)}</span>
                                    {renderTools(c)}
                                </div>
                                {editingId === c.id
                                    ? (
                                        <Input.TextArea
                                            autoSize={{ minRows: 2, maxRows: 10 }}
                                            value={editText}
                                            onChange={(e) => setEditText(e.target.value)}
                                            onKeyDown={(e) => {
                                                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onSaveEdit(c.id);
                                                }
                                            }}
                                        />
                                    )
                                    : c.body && <div className="task-msg-body">{c.body}</div>}
                                {renderAttachments(c)}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className={`task-composer${dragOver ? ' dragover' : ''}`} {...dropHandlers}>
                <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 8 }}
                    placeholder="Напишите комментарий… Файлы можно перетащить сюда или вставить из буфера"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onPaste={onPaste}
                    onKeyDown={onComposerKeyDown}
                />
                {files.length > 0 && (
                    <div className="task-composer-files">
                        {files.map((f, i) => (
                            <Tag
                                key={`${f.name}-${f.lastModified}-${i}`}
                                closable
                                icon={f.type?.startsWith('image/') ? <FileImageOutlined /> : <PaperClipOutlined />}
                                onClose={() => setFiles((l) => l.filter((x) => x !== f))}
                            >
                                {f.name} <span style={{ color: '#bfbfbf' }}>{fmtSize(f.size)}</span>
                            </Tag>
                        ))}
                    </div>
                )}
                <div className="task-composer-bar">
                    <Upload multiple showUploadList={false} beforeUpload={(f) => { addFiles([f]); return false; }}>
                        <Tooltip title="Прикрепить файлы">
                            <Button type="text" size="small" icon={<PaperClipOutlined />} />
                        </Tooltip>
                    </Upload>
                    <span>Ctrl+Enter — отправить</span>
                    <Button
                        type="primary"
                        icon={<SendOutlined />}
                        loading={sending}
                        disabled={!text.trim() && !files.length}
                        onClick={onAdd}
                    >
                        Отправить
                    </Button>
                </div>
                {dragOver && (
                    <div className="task-composer-drop"><CloudUploadOutlined /> Отпустите, чтобы прикрепить</div>
                )}
            </div>
            <PdfModal file={pdfModal} onClose={() => setPdfModal(null)} />
        </div>
    );
};

export default TaskComments;
